import {
  Text,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary14SemiBold,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { toRevenueRecognitionMethodLabel } from 'modules/RevenueRecognition'
import { useProduct } from 'modules/Products/hooks/useProduct'
import { useShouldShowXeroMappings } from 'modules/Products/hooks/useShouldShowXeroMappings'
import {
  formatXeroMappingOption,
  XERO_MAPPING_LABELS
} from 'Products/constants/xeroMapping.constants'
import { ProductXeroLedgerAccountMapping } from 'Products/types'

export const ProductMeta = () => {
  const { product } = useProduct()
  const taxCategoriesQuery = useQuery(
    dashboard20240730Client.getTaxCategories,
    undefined,
    { staleTime: 5 * MINUTES }
  )
  const showXeroMappings = useShouldShowXeroMappings()

  if (!product) return null

  const taxCategoryName = taxCategoriesQuery.data?.items.find(
    category => category.id === product.taxCategoryId
  )?.name

  const deferredRevenueMapping = product.xeroLedgerAccountMappings?.find(
    m => m.mappableLedgerAccount === 'DEFERRED_REVENUE'
  )
  const recognizedRevenueMapping = product.xeroLedgerAccountMappings?.find(
    m => m.mappableLedgerAccount === 'RECOGNIZED_REVENUE'
  )
  const unbilledRevenueMapping = product.xeroLedgerAccountMappings?.find(
    m => m.mappableLedgerAccount === 'UNBILLED_REVENUE'
  )

  const hasExtraRows = Boolean(
    product?.revenueRecognitionMethod || showXeroMappings
  )

  return (
    <Flex flexDirection="column">
      <Text {...Primary14SemiBold} color={GreyGrey80}>
        Details
      </Text>
      <Grid
        templateColumns={`${hasExtraRows ? '200px' : '150px'} 1fr`}
        gap={4}
        mt={4}
        color={GreyGrey80}
      >
        <Text {...Primary13Regular}>Name</Text>
        <Text {...Primary13Regular} data-testid="product.name">
          {product.name}
        </Text>
        <Text {...Primary13Regular}>Description</Text>
        <Text
          {...Primary13Regular}
          sx={{
            textColor: product.label ? GreyGrey80 : GreyGrey60
          }}
          data-testid="product.description"
          whiteSpace="pre-line"
          wordBreak="break-word"
        >
          {product.label || 'Not set'}
        </Text>
        {product.revenueRecognitionMethod && (
          <>
            <Text {...Primary13Regular}>Revenue recognition method</Text>
            <Text
              {...Primary13Regular}
              data-testid="product.revenueRecognitionMethod"
            >
              {toRevenueRecognitionMethodLabel(
                product.revenueRecognitionMethod
              )}
            </Text>
          </>
        )}
        {showXeroMappings && (
          <>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.DEFERRED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              sx={{
                textColor: deferredRevenueMapping ? GreyGrey80 : GreyGrey60
              }}
              data-testid="product.deferredRevenueXeroAccount"
            >
              {formatMappingDisplay(deferredRevenueMapping)}
            </Text>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.RECOGNIZED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              sx={{
                textColor: recognizedRevenueMapping ? GreyGrey80 : GreyGrey60
              }}
              data-testid="product.recognizedRevenueXeroAccount"
            >
              {formatMappingDisplay(recognizedRevenueMapping)}
            </Text>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.UNBILLED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              sx={{
                textColor: unbilledRevenueMapping ? GreyGrey80 : GreyGrey60
              }}
              data-testid="product.unbilledRevenueXeroAccount"
            >
              {formatMappingDisplay(unbilledRevenueMapping)}
            </Text>
          </>
        )}
        <Text {...Primary13Regular}>Updated</Text>
        <Text {...Primary13Regular} data-testid="product.updatedAt">
          {dateTimeWithFormat(product.updatedAt, 'dd MMM yyyy')}
        </Text>
        <Text {...Primary13Regular}>Created</Text>
        <Text {...Primary13Regular} data-testid="product.createdAt">
          {dateTimeWithFormat(product.createdAt, 'dd MMM yyyy')}
        </Text>
        <Text {...Primary13Regular}>Tax category</Text>
        <Text
          {...Primary13Regular}
          sx={{
            textColor: taxCategoryName ? GreyGrey80 : GreyGrey60
          }}
          data-testid="product.taxCategory"
        >
          {taxCategoryName ?? 'Not set'}
        </Text>
      </Grid>
    </Flex>
  )
}

const formatMappingDisplay = (
  mapping: ProductXeroLedgerAccountMapping | undefined
): string =>
  mapping
    ? formatXeroMappingOption(
        mapping.xeroLedgerAccountCode,
        mapping.xeroLedgerAccountName
      )
    : 'Not set'
